import { useLingui } from '@lingui/react/macro'
import { useCallback, useEffect, useRef, useState } from 'react'

const CONFIRMATION_VISIBLE_MS = 2500

export interface DocChatFeedbackDialogState {
  isOpen: boolean
  isSubmitting: boolean
  submitted: boolean
  error: string | null
  open: () => void
  close: () => void
  onOpenChange: (open: boolean) => void
  /** Runs `send`; on success shows the confirmation and closes after a moment. */
  submit: (send: () => Promise<unknown>) => Promise<boolean>
}

/** Open, submit, confirm and close flow shared by every DocChat feedback dialog. */
export function useDocChatFeedbackDialog(): DocChatFeedbackDialogState {
  const { t } = useLingui()
  const [isOpen, setIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  useEffect(() => clearCloseTimeout, [clearCloseTimeout])

  const close = useCallback(() => {
    clearCloseTimeout()
    setIsOpen(false)
    setSubmitted(false)
    setError(null)
  }, [clearCloseTimeout])

  const open = useCallback(() => {
    clearCloseTimeout()
    setSubmitted(false)
    setError(null)
    setIsOpen(true)
  }, [clearCloseTimeout])

  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!next && !isSubmitting) close()
    },
    [close, isSubmitting]
  )

  const submit = useCallback(
    async (send: () => Promise<unknown>) => {
      setIsSubmitting(true)
      setError(null)
      try {
        await send()
        setSubmitted(true)
        closeTimeoutRef.current = window.setTimeout(
          close,
          CONFIRMATION_VISIBLE_MS
        )
        return true
      } catch {
        setError(t`Could not send your feedback. Please try again.`)
        return false
      } finally {
        setIsSubmitting(false)
      }
    },
    [close, t]
  )

  return {
    isOpen,
    isSubmitting,
    submitted,
    error,
    open,
    close,
    onOpenChange,
    submit,
  }
}
